import { useState, useEffect, useCallback } from 'react';

const USE_MOCK = process.env.REACT_APP_USE_MOCK === 'true';

// ── Default seed data ──

const DEFAULT_ASSUMPTIONS = {
  jpyPerSgd: 111,
  jpyPerUsd: 150,
  seasonDays: 120,
  dailyCapacity: 1500,
  liftPassFull: 6500,
  liftPassHalf: 4200,
  pctFullDay: 0.65,
  pctHalfDay: 0.25,
  accomPerVisitor: 12000,
  pctAccom: 0.3,
  fnbPerVisitor: 3500,
  skiSchoolPerVisitor: 8000,
  pctSkiSchool: 0.2,
  retailPerVisitor: 2000,
  seasonPassBase: 80,
  revGrowth: 0.03,
  opexInflation: 0.025,
  contingency: 0.1,
  wacc: 0.1,
  taxRate: 0.3,
  debtPct: 0.4,
  interestRate: 0.03,
  deprecYears: 25,
};

const DEFAULT_CAPEX = [
  {
    name: 'Land & Site Preparation',
    items: [
      { name: 'Land acquisition & clearing', phase1: 800, phase2: 0, phase3: 0 },
      { name: 'Access roads & parking', phase1: 350, phase2: 100, phase3: 0 },
      { name: 'Grading & drainage', phase1: 200, phase2: 50, phase3: 0 },
    ],
  },
  {
    name: 'Ski Infrastructure',
    items: [
      { name: 'Chairlifts (4x)', phase1: 2400, phase2: 1200, phase3: 800 },
      { name: 'Snowmaking system', phase1: 1500, phase2: 500, phase3: 300 },
      { name: 'Trail grooming equipment', phase1: 400, phase2: 200, phase3: 100 },
    ],
  },
  {
    name: 'Buildings & Facilities',
    items: [
      { name: 'Base lodge', phase1: 1200, phase2: 0, phase3: 0 },
      { name: 'Hotel (100 rooms)', phase1: 3000, phase2: 1500, phase3: 0 },
      { name: 'F&B outlets', phase1: 500, phase2: 300, phase3: 200 },
      { name: 'Ski school facility', phase1: 300, phase2: 0, phase3: 0 },
      { name: 'Retail & rental shop', phase1: 250, phase2: 0, phase3: 0 },
      { name: 'Maintenance building', phase1: 200, phase2: 0, phase3: 0 },
      { name: 'Staff accommodation', phase1: 400, phase2: 200, phase3: 0 },
    ],
  },
  {
    name: 'Technology & Systems',
    items: [
      { name: 'RFID ticketing system', phase1: 150, phase2: 50, phase3: 0 },
      { name: 'POS & booking systems', phase1: 80, phase2: 30, phase3: 0 },
      { name: 'CCTV & security', phase1: 100, phase2: 40, phase3: 0 },
      { name: 'Wi-Fi & networking', phase1: 60, phase2: 20, phase3: 0 },
    ],
  },
  {
    name: 'Design & Advisory',
    items: [
      { name: 'Architecture & engineering', phase1: 400, phase2: 100, phase3: 0 },
      { name: 'Environmental impact assessment', phase1: 150, phase2: 0, phase3: 0 },
      { name: 'Project management', phase1: 300, phase2: 150, phase3: 75 },
      { name: 'Legal & permitting', phase1: 200, phase2: 50, phase3: 25 },
    ],
  },
];

const DEFAULT_OPEX = [
  {
    name: 'Staffing',
    items: [
      { name: 'Full-time staff (operations)', annual: 450 },
      { name: 'Seasonal staff', annual: 280 },
      { name: 'Management team', annual: 180 },
    ],
  },
  {
    name: 'Utilities & Energy',
    items: [
      { name: 'Electricity (snowmaking + lifts)', annual: 200 },
      { name: 'Water & sewage', annual: 40 },
      { name: 'Heating & cooling', annual: 60 },
    ],
  },
  {
    name: 'Maintenance & Repairs',
    items: [
      { name: 'Lift maintenance', annual: 120 },
      { name: 'Building maintenance', annual: 80 },
      { name: 'Equipment servicing', annual: 60 },
      { name: 'Trail maintenance', annual: 40 },
    ],
  },
  {
    name: 'Marketing & Sales',
    items: [
      { name: 'Digital marketing', annual: 80 },
      { name: 'Events & partnerships', annual: 40 },
      { name: 'Sales team', annual: 60 },
    ],
  },
  {
    name: 'Insurance & Compliance',
    items: [
      { name: 'Property & liability insurance', annual: 100 },
      { name: 'Regulatory compliance', annual: 30 },
    ],
  },
  {
    name: 'Administration & Overheads',
    items: [
      { name: 'Office & admin costs', annual: 50 },
      { name: 'IT & software', annual: 30 },
      { name: 'Professional services', annual: 40 },
    ],
  },
];

const GYM_ASSUMPTIONS = {
  jpyPerSgd: 111,
  jpyPerUsd: 150,
  seasonDays: 365,
  dailyCapacity: 30,
  liftPassFull: 0,
  liftPassHalf: 0,
  pctFullDay: 0,
  pctHalfDay: 0,
  accomPerVisitor: 0,
  pctAccom: 0,
  fnbPerVisitor: 0,
  skiSchoolPerVisitor: 0,
  pctSkiSchool: 0,
  retailPerVisitor: 0,
  seasonPassBase: 0,
  revGrowth: 0.05,
  opexInflation: 0.03,
  contingency: 0.05,
  wacc: 0.08,
  taxRate: 0.17,
  debtPct: 0.3,
  interestRate: 0.04,
  deprecYears: 10,
};

const GYM_CAPEX = [
  {
    name: 'Fit-Out & Equipment',
    items: [
      { name: 'Renovation & fit-out', phase1: 0.15, phase2: 0, phase3: 0 },
      { name: 'Training equipment', phase1: 0.05, phase2: 0.02, phase3: 0 },
      { name: 'Flooring & ring', phase1: 0.03, phase2: 0, phase3: 0 },
    ],
  },
  {
    name: 'Technology',
    items: [
      { name: 'Booking & POS system', phase1: 0.01, phase2: 0, phase3: 0 },
      { name: 'CCTV & access control', phase1: 0.008, phase2: 0, phase3: 0 },
    ],
  },
];

const GYM_OPEX = [
  {
    name: 'Staffing',
    items: [
      { name: 'Trainers', annual: 0.048 },
      { name: 'Front desk', annual: 0.024 },
    ],
  },
  {
    name: 'Rent & Utilities',
    items: [
      { name: 'Rent', annual: 0.036 },
      { name: 'Utilities', annual: 0.006 },
    ],
  },
  {
    name: 'Marketing',
    items: [
      { name: 'Digital marketing', annual: 0.006 },
      { name: 'Events', annual: 0.003 },
    ],
  },
];

const MOCK_BUSINESSES = [
  {
    id: 'ski-resort-1',
    name: 'Niseko Highland Resort',
    type: 'Ski Resort',
    ownershipPct: 60,
    isProject: false,
    currencies: ['JPY', 'USD', 'SGD'],
    roles: { 'mock-user-1': 'owner' },
    assumptions: { ...DEFAULT_ASSUMPTIONS },
    capex: JSON.parse(JSON.stringify(DEFAULT_CAPEX)),
    opex: JSON.parse(JSON.stringify(DEFAULT_OPEX)),
    claims: [
      {
        id: 'claim-1',
        description: 'Site survey travel expenses',
        amount: 85000,
        currency: 'JPY',
        category: 'Travel',
        fromAccount: 'NK Operating',
        date: '2026-03-15',
        status: 'pending',
        receiptUrl: null,
      },
    ],
    pettyCash: { float: 500000, currency: 'JPY' },
    documents: [],
  },
  {
    id: 'gym-1',
    name: 'Khao Noi Gym',
    type: 'Muay Thai Gym',
    ownershipPct: 100,
    isProject: false,
    currencies: ['SGD', 'USD'],
    roles: { 'mock-user-1': 'owner' },
    assumptions: { ...GYM_ASSUMPTIONS },
    capex: JSON.parse(JSON.stringify(GYM_CAPEX)),
    opex: JSON.parse(JSON.stringify(GYM_OPEX)),
    claims: [
      {
        id: 'claim-2',
        description: 'New heavy bag replacement',
        amount: 350,
        currency: 'SGD',
        category: 'Equipment',
        fromAccount: 'KNG Petty Cash',
        date: '2026-04-01',
        status: 'submitted',
        receiptUrl: null,
      },
      {
        id: 'claim-3',
        description: 'Monthly cleaning supplies',
        amount: 45,
        currency: 'SGD',
        category: 'Supplies',
        fromAccount: 'KNG Petty Cash',
        date: '2026-04-03',
        status: 'received',
        receiptUrl: null,
      },
    ],
    pettyCash: { float: 500, currency: 'SGD' },
    documents: [],
  },
  {
    id: 'project-1',
    name: 'Reefs & Rivers',
    type: 'Fishing Charters',
    ownershipPct: 0,
    isProject: true,
    currencies: ['SGD', 'USD', 'IDR', 'MVR'],
    roles: { 'mock-user-1': 'owner' },
    assumptions: { ...DEFAULT_ASSUMPTIONS, taxRate: 0.17 },
    capex: [],
    opex: [],
    claims: [],
    pettyCash: { float: 1000, currency: 'SGD' },
    documents: [],
  },
];

// ── Single hook with mock/firebase branching ──

export default function useBusinessData(user) {
  const userId = user?.uid;
  const userEmail = user?.email;
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    if (USE_MOCK) {
      setBusinesses(JSON.parse(JSON.stringify(MOCK_BUSINESSES)));
      setLoading(false);
      return;
    }

    let unsubscribe;
    async function init() {
      try {
        const { db } = await import('../firebase/config');
        const { collection, query, where, onSnapshot, getDocs, doc, updateDoc } = await import('firebase/firestore');

        // ── Auto-claim any pending invites for this user before subscribing ──
        if (userEmail) {
          try {
            const inviteQ = query(
              collection(db, 'pendingInvites'),
              where('email', '==', userEmail.toLowerCase().trim()),
              where('claimed', '==', false),
            );
            const inviteSnap = await getDocs(inviteQ);
            for (const inviteDoc of inviteSnap.docs) {
              const invite = inviteDoc.data();
              try {
                await updateDoc(doc(db, 'businesses', invite.businessId), {
                  [`roles.${userId}`]: invite.role,
                });
                await updateDoc(doc(db, 'pendingInvites', inviteDoc.id), {
                  claimed: true,
                  claimedBy: userId,
                  claimedAt: new Date().toISOString(),
                });
              } catch (err) {
                console.error('Failed to claim invite', invite, err);
              }
            }
          } catch (err) {
            console.error('Pending invite check failed:', err);
          }
        }

        // All roles that grant access to a business
        const ALL_ROLES = ['owner', 'viewer', 'ops_staff', 'content_manager', 'biz_dev', 'trainer', 'accountant'];

        const q = query(
          collection(db, 'businesses'),
          where(`roles.${userId}`, 'in', ALL_ROLES)
        );

        unsubscribe = onSnapshot(q, (snapshot) => {
          const docs = snapshot.docs
            .map((d) => ({
              id: d.id,
              ...d.data(),
            }))
            .filter((d) => !d.archived);
          setBusinesses(docs);
          setLoading(false);
        });
      } catch (err) {
        console.error('Firestore init failed:', err);
        setLoading(false);
      }
    }
    init();
    return () => unsubscribe && unsubscribe();
  }, [userId, userEmail]);

  const getBusiness = useCallback(
    (id) => businesses.find((b) => b.id === id) || null,
    [businesses]
  );

  const createBusiness = useCallback(
    async (data) => {
      const EMPTY_ASSUMPTIONS = {
        jpyPerSgd: 111, jpyPerUsd: 150,
        revGrowth: 0.03, opexInflation: 0.025, contingency: 0.1,
        wacc: 0.1, taxRate: 0.17, debtPct: 0, interestRate: 0.03, deprecYears: 10,
      };

      if (USE_MOCK) {
        const newBiz = {
          id: `biz-${Date.now()}`,
          roles: { [userId]: 'owner' },
          assumptions: { ...EMPTY_ASSUMPTIONS },
          capex: [],
          opex: [],
          claims: [],
          pettyCash: { float: 0, currency: 'SGD' },
          documents: [],
          ...data,
        };
        setBusinesses((prev) => [...prev, newBiz]);
        return newBiz.id;
      }

      const { db } = await import('../firebase/config');
      const { collection, addDoc } = await import('firebase/firestore');
      const docRef = await addDoc(collection(db, 'businesses'), {
        roles: { [userId]: 'owner' },
        assumptions: { ...EMPTY_ASSUMPTIONS },
        capex: [],
        opex: [],
        claims: [],
        pettyCash: { float: 0, currency: 'SGD' },
        documents: [],
        ...data,
      });
      return docRef.id;
    },
    [userId]
  );

  const deleteBusiness = useCallback(async (id) => {
    if (USE_MOCK) {
      setBusinesses((prev) => prev.filter((b) => b.id !== id));
      return;
    }
    const { db } = await import('../firebase/config');
    const { doc, deleteDoc } = await import('firebase/firestore');
    await deleteDoc(doc(db, 'businesses', id));
  }, []);

  const updateBusiness = useCallback(async (id, updates) => {
    if (USE_MOCK) {
      setBusinesses((prev) => prev.map((b) => (b.id === id ? { ...b, ...updates } : b)));
      return;
    }
    const { db } = await import('../firebase/config');
    const { doc, updateDoc } = await import('firebase/firestore');
    await updateDoc(doc(db, 'businesses', id), updates);
  }, []);

  const updateCapex = useCallback(async (id, capex) => {
    if (USE_MOCK) {
      setBusinesses((prev) => prev.map((b) => (b.id === id ? { ...b, capex } : b)));
      return;
    }
    const { db } = await import('../firebase/config');
    const { doc, updateDoc } = await import('firebase/firestore');
    await updateDoc(doc(db, 'businesses', id), { capex });
  }, []);

  const updateOpex = useCallback(async (id, opex) => {
    if (USE_MOCK) {
      setBusinesses((prev) => prev.map((b) => (b.id === id ? { ...b, opex } : b)));
      return;
    }
    const { db } = await import('../firebase/config');
    const { doc, updateDoc } = await import('firebase/firestore');
    await updateDoc(doc(db, 'businesses', id), { opex });
  }, []);

  const updateAssumptions = useCallback(async (id, assumptions) => {
    if (USE_MOCK) {
      setBusinesses((prev) => prev.map((b) => (b.id === id ? { ...b, assumptions } : b)));
      return;
    }
    const { db } = await import('../firebase/config');
    const { doc, updateDoc } = await import('firebase/firestore');
    await updateDoc(doc(db, 'businesses', id), { assumptions });
  }, []);

  const updateClaims = useCallback(async (id, claims) => {
    if (USE_MOCK) {
      setBusinesses((prev) => prev.map((b) => (b.id === id ? { ...b, claims } : b)));
      return;
    }
    const { db } = await import('../firebase/config');
    const { doc, updateDoc } = await import('firebase/firestore');
    await updateDoc(doc(db, 'businesses', id), { claims });
  }, []);

  const updatePettyCash = useCallback(async (id, pettyCash) => {
    if (USE_MOCK) {
      setBusinesses((prev) => prev.map((b) => (b.id === id ? { ...b, pettyCash } : b)));
      return;
    }
    const { db } = await import('../firebase/config');
    const { doc, updateDoc } = await import('firebase/firestore');
    await updateDoc(doc(db, 'businesses', id), { pettyCash });
  }, []);

  const updateDocuments = useCallback(async (id, documents) => {
    if (USE_MOCK) {
      setBusinesses((prev) => prev.map((b) => (b.id === id ? { ...b, documents } : b)));
      return;
    }
    const { db } = await import('../firebase/config');
    const { doc, updateDoc } = await import('firebase/firestore');
    await updateDoc(doc(db, 'businesses', id), { documents });
  }, []);

  const uploadDocument = useCallback(async (businessId, file, metadata) => {
    if (USE_MOCK) {
      // Simulate upload with a fake URL
      const fakeUrl = `mock://uploads/${businessId}/${file.name}`;
      const newDoc = {
        id: `doc-${Date.now()}`,
        name: file.name,
        size: file.size,
        type: file.type,
        url: fakeUrl,
        uploadedAt: new Date().toISOString(),
        ...metadata,
      };
      setBusinesses((prev) =>
        prev.map((b) =>
          b.id === businessId
            ? { ...b, documents: [...(b.documents || []), newDoc] }
            : b
        )
      );
      return newDoc;
    }

    // Firebase Storage upload
    const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
    const storage = getStorage();
    const storageRef = ref(storage, `businesses/${businessId}/documents/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);

    const newDoc = {
      id: `doc-${Date.now()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      url,
      uploadedAt: new Date().toISOString(),
      ...metadata,
    };

    const { db } = await import('../firebase/config');
    const { doc: firestoreDoc, updateDoc: firestoreUpdate, arrayUnion } = await import('firebase/firestore');
    await firestoreUpdate(firestoreDoc(db, 'businesses', businessId), {
      documents: arrayUnion(newDoc),
    });

    return newDoc;
  }, []);

  // Create a pending invite. The invitee will get access when they next sign in.
  const inviteToBusiness = useCallback(async (businessId, email, role) => {
    if (USE_MOCK) {
      console.log(`[Mock] Invited ${email} to ${businessId} as ${role}`);
      return;
    }
    const { db } = await import('../firebase/config');
    const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
    await addDoc(collection(db, 'pendingInvites'), {
      email: email.toLowerCase().trim(),
      businessId,
      role,
      invitedBy: userId,
      createdAt: serverTimestamp(),
      claimed: false,
    });
  }, [userId]);

  // List pending invites for a specific business (so owner sees who's been invited)
  const getPendingInvites = useCallback(async (businessId) => {
    if (USE_MOCK) return [];
    const { db } = await import('../firebase/config');
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const q = query(
      collection(db, 'pendingInvites'),
      where('businessId', '==', businessId),
      where('claimed', '==', false),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }, []);

  // Revoke a pending invite (delete it before claim)
  const revokeInvite = useCallback(async (inviteId) => {
    if (USE_MOCK) return;
    const { db } = await import('../firebase/config');
    const { doc, deleteDoc } = await import('firebase/firestore');
    await deleteDoc(doc(db, 'pendingInvites', inviteId));
  }, []);

  // Revoke an existing user's access (remove from roles)
  const revokeAccess = useCallback(async (businessId, uidToRemove) => {
    if (USE_MOCK) return;
    const { db } = await import('../firebase/config');
    const { doc, updateDoc, deleteField } = await import('firebase/firestore');
    await updateDoc(doc(db, 'businesses', businessId), {
      [`roles.${uidToRemove}`]: deleteField(),
    });
  }, []);

  return {
    businesses,
    loading,
    getBusiness,
    createBusiness,
    deleteBusiness,
    updateBusiness,
    updateCapex,
    updateOpex,
    updateAssumptions,
    updateClaims,
    updatePettyCash,
    updateDocuments,
    uploadDocument,
    inviteToBusiness,
    getPendingInvites,
    revokeInvite,
    revokeAccess,
  };
}

export { DEFAULT_ASSUMPTIONS, DEFAULT_CAPEX, DEFAULT_OPEX };
